import React, { useState, useEffect } from "react";
import { RiFlowChart } from "react-icons/ri";
import { useSubscription } from "../hooks/useSubscription";

// Type-safe wrapper for react-icons to satisfy TS2786
const FlowChartIcon = RiFlowChart as React.FC<React.SVGProps<SVGSVGElement>>;
import { useNavigate, useSearchParams } from "react-router-dom";
import { workflowService } from "../services/workflowService";
import { WorkflowTemplate } from "../types/workflow";
import WorkflowTemplateCard from "../components/workflow/WorkflowTemplateCard";
import WorkflowList from "../components/workflow/WorkflowList";
import CreateWorkflowModal from "../components/workflow/CreateWorkflowModal";
import { getIntegrationLabel } from "../utils/integrationLabels";
import { useMobile } from "../@/hooks/use-mobile";
import "./WorkflowManager.module.css";

const WorkflowManager: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useMobile(); // Keep for specific mobile-only features
  const { isFree: isFreePlan, loading: subscriptionLoading } = useSubscription();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<
    "templates" | "workflows" | "analytics"
  >(() => {
    const tabParam = searchParams.get("tab");
    return tabParam === "templates" ||
      tabParam === "workflows" ||
      tabParam === "analytics"
      ? (tabParam as any)
      : "templates";
  });
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [workflows, setWorkflows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] =
    useState<WorkflowTemplate | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filters, setFilters] = useState({
    category: "",
    complexity: "",
    integrations: [] as string[],
  });

  useEffect(() => {
    if (!subscriptionLoading) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionLoading]);

  // Keep URL in sync with active tab
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (params.get("tab") !== activeTab) {
      params.set("tab", activeTab);
      setSearchParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // React to URL changes (back/forward) and update activeTab
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (
      (tabParam === "templates" ||
        tabParam === "workflows" ||
        tabParam === "analytics") &&
      tabParam !== activeTab
    ) {
      setActiveTab(tabParam as any);
    }
  }, [searchParams]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load templates (always) and user workflows (skipped for free plan — endpoint requires Basic+)
      const templatesData = await workflowService.getTemplates(filters);
      const workflowsData = isFreePlan ? [] : await workflowService.getUserWorkflows();

      // Ensure we always have arrays
      setTemplates(Array.isArray(templatesData) ? templatesData : []);
      setWorkflows(Array.isArray(workflowsData) ? workflowsData : []);
    } catch (error) {
      console.error("Error loading workflow data:", error);
      // Set empty arrays on error
      setTemplates([]);
      setWorkflows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWorkflow = (template: WorkflowTemplate) => {
    setSelectedTemplate(template);
    setShowCreateModal(true);
  };

  const handleWorkflowCreated = (workflow: any) => {
    setWorkflows((prev) => [workflow, ...prev]);
    setShowCreateModal(false);
    setSelectedTemplate(null);
  };


  const handleFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
    loadData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#303030]"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
      {/* Header */}
      <div className="mb-6 lg:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
          Workflow Automation
        </h1>
        <p className="mt-2 text-sm sm:text-base text-gray-600">
          Automate investor updates, stakeholder reports, and team
          communications with AI-powered workflows
        </p>

        {/* New Features Highlight */}
        {/* <div className="mt-4 p-4 bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <span className="text-lg">✨</span>
            <h3 className="text-sm font-semibold text-indigo-900">
              New Workflow Templates
            </h3>
          </div>
          <div className="text-sm text-indigo-700">
            <span className="font-medium">Investor Weekly Updates</span> and{" "}
            <span className="font-medium">
              Stakeholder Product Progress Reports
            </span>{" "}
            - Automatically collect KPIs, revenue data, and development progress
            to create professional reports for your stakeholders.
          </div>
        </div> */}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 mt-4">
          <button
            onClick={() => navigate("/updates/new")}
            className="inline-flex items-center px-4 py-2 bg-[#303030] text-white font-medium rounded-md hover:bg-[#1f1f1f] focus:outline-none focus:ring-2 focus:ring-gray-700 focus:ring-offset-2 transition-colors"
          >
            <FlowChartIcon className="w-4 h-4 mr-2" aria-hidden />
            Create Workflow
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 mb-6 lg:mb-8">
        <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto scrollbar-hide">
          {[
            { id: "templates", label: "Templates", count: templates.length },
            { id: "workflows", label: "My Workflows", count: workflows.length },
            { id: "analytics", label: "Analytics", count: null },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === tab.id
                  ? "border-gray-800 text-gray-900"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {tab.label}
              {tab.count !== null && (
                <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      {activeTab === "templates" && (
        <div>
          {/* Filters */}
          <WorkflowFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            availableIntegrations={[
              "jira",
              "github",
              "asana",
              "slack",
              "notion",
              "google-sheets",
              "stripe",
              "salesforce",
              "hubspot",
              "linear",
              "zendesk",
              "typeform",
              "twitter",
            ]}
          />

          {/* Templates Grid - Responsive: 1 col mobile, 2 col tablet, 3 col desktop */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6 mt-4 md:mt-6">
            {templates.map((template) => (
              <WorkflowTemplateCard
                key={template.id}
                template={template}
                onCreateWorkflow={handleCreateWorkflow}
              />
            ))}
          </div>

          {templates.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">📋</div>
              <div className="text-gray-600 text-base lg:text-lg mb-2">
                No workflow templates match your criteria
              </div>
              <div className="text-gray-500 text-sm mb-6">
                Try adjusting your filters to see more templates
              </div>
              <button
                onClick={() =>
                  setFilters({ category: "", complexity: "", integrations: [] })
                }
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-[#303030] bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-700 focus:ring-offset-2"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === "workflows" && (
        <WorkflowList
          workflows={workflows}
          onWorkflowUpdate={loadData}
          onCreateNew={() => setActiveTab("templates")}
        />
      )}

      {activeTab === "analytics" && <WorkflowAnalytics />}

      {/* Create Workflow Modal */}
      {showCreateModal && selectedTemplate && (
        <CreateWorkflowModal
          template={selectedTemplate}
          onClose={() => setShowCreateModal(false)}
          onWorkflowCreated={handleWorkflowCreated}
        />
      )}

    </div>
  );
};

// Workflow Filters Component
interface WorkflowFiltersProps {
  filters: {
    category: string;
    complexity: string;
    integrations: string[];
  };
  onFilterChange: (filters: any) => void;
  availableIntegrations: string[];
}

const WorkflowFilters: React.FC<WorkflowFiltersProps> = ({
  filters,
  onFilterChange,
  availableIntegrations,
}) => {
  const categories = [
    { value: "", label: "All Categories" },
    { value: "data_collection", label: "Data Collection" },
    { value: "analysis", label: "Analysis & Reporting" },
    { value: "notification", label: "Stakeholder Updates" },
    { value: "integration", label: "System Integration" },
  ];

  const complexities = [
    { value: "", label: "All Levels" },
    { value: "simple", label: "Simple" },
    { value: "medium", label: "Medium" },
    { value: "complex", label: "Complex" },
  ];

  return (
    <div className="bg-gray-50 p-4 rounded-lg">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Category Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category
          </label>
          <select
            value={filters.category}
            onChange={(e) =>
              onFilterChange({ ...filters, category: e.target.value })
            }
            className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white text-sm"
          >
            {categories.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>

        {/* Complexity Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Complexity
          </label>
          <select
            value={filters.complexity}
            onChange={(e) =>
              onFilterChange({ ...filters, complexity: e.target.value })
            }
            className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white text-sm"
          >
            {complexities.map((comp) => (
              <option key={comp.value} value={comp.value}>
                {comp.label}
              </option>
            ))}
          </select>
        </div>

        {/* Integrations Filter */}
        <div className="md:col-span-2 lg:col-span-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Required Integrations
          </label>
          <IntegrationsDropdown
            selectedIntegrations={filters.integrations}
            availableIntegrations={availableIntegrations}
            onSelectionChange={(selectedIntegrations) =>
              onFilterChange({ ...filters, integrations: selectedIntegrations })
            }
          />
        </div>
      </div>
    </div>
  );
};

// Integrations Dropdown Component
interface IntegrationsDropdownProps {
  selectedIntegrations: string[];
  availableIntegrations: string[];
  onSelectionChange: (selectedIntegrations: string[]) => void;
}

const IntegrationsDropdown: React.FC<IntegrationsDropdownProps> = ({
  selectedIntegrations,
  availableIntegrations,
  onSelectionChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggleIntegration = (integration: string) => {
    const isSelected = selectedIntegrations.includes(integration);
    const newSelection = isSelected
      ? selectedIntegrations.filter((i) => i !== integration)
      : [...selectedIntegrations, integration];
    onSelectionChange(newSelection);
  };

  const clearAllSelections = () => {
    onSelectionChange([]);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white text-sm text-left flex items-center justify-between focus:ring-gray-700 focus:border-gray-400"
      >
        <span className="truncate">
          {selectedIntegrations.length === 0
            ? "Select integrations..."
            : selectedIntegrations.length === 1
            ? getIntegrationLabel(selectedIntegrations[0])
            : `${selectedIntegrations.length} integrations selected`}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {selectedIntegrations.length > 0 && (
            <div className="px-3 py-2 border-b border-gray-200">
              <button
                onClick={clearAllSelections}
                className="text-xs text-[#303030] hover:text-gray-900"
              >
                Clear all
              </button>
            </div>
          )}
          <div className="py-1">
            {availableIntegrations.map((integration) => (
              <label
                key={integration}
                className="flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={selectedIntegrations.includes(integration)}
                  onChange={() => handleToggleIntegration(integration)}
                  className="rounded border-gray-300 text-[#303030] focus:ring-gray-700 mr-3"
                />
                <span className="text-sm text-gray-700 flex-1">
                  {getIntegrationLabel(integration)}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Overlay to close dropdown when clicking outside */}
      {isOpen && (
        <div className="fixed inset-0 z-0" onClick={() => setIsOpen(false)} />
      )}
    </div>
  );
};

// Workflow Analytics Component - keeping existing implementation with responsive improvements
const WorkflowAnalytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const data = await workflowService.getAnalytics();
      setAnalytics(data);
    } catch (error) {
      console.error("Error loading analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="animate-pulse">Loading analytics...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <div className="bg-white p-4 lg:p-6 rounded-lg shadow">
          <div className="text-xl lg:text-2xl font-bold text-gray-900">
            {analytics?.totalExecutions || 0}
          </div>
          <div className="text-xs lg:text-sm text-gray-600">
            Total Executions
          </div>
        </div>
        <div className="bg-white p-4 lg:p-6 rounded-lg shadow">
          <div className="text-xl lg:text-2xl font-bold text-green-600">
            {analytics?.successfulExecutions || 0}
          </div>
          <div className="text-xs lg:text-sm text-gray-600">Successful</div>
        </div>
        <div className="bg-white p-4 lg:p-6 rounded-lg shadow">
          <div className="text-xl lg:text-2xl font-bold text-blue-600">
            {analytics?.successRate || 0}%
          </div>
          <div className="text-xs lg:text-sm text-gray-600">Success Rate</div>
        </div>
        <div className="bg-white p-4 lg:p-6 rounded-lg shadow">
          <div className="text-xl lg:text-2xl font-bold text-purple-600">
            {analytics?.averageProcessingTime || 0}ms
          </div>
          <div className="text-xs lg:text-sm text-gray-600">
            Avg. Processing Time
          </div>
        </div>
      </div>

      {/* Recent Executions */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-4 py-4 lg:px-6 lg:py-4 border-b border-gray-200">
          <h3 className="text-base lg:text-lg font-medium text-gray-900">
            Recent Executions
          </h3>
        </div>
        <div className="divide-y divide-gray-200">
          {analytics?.recentExecutions
            ?.slice(0, 10)
            .map((execution: any, index: number) => (
              <div
                key={index}
                className="px-4 py-4 lg:px-6 lg:py-4 flex items-center justify-between"
              >
                <div className="flex items-center space-x-3">
                  <div
                    className={`w-3 h-3 rounded-full ${
                      execution.status === "completed"
                        ? "bg-green-400"
                        : "bg-red-400"
                    }`}
                  ></div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {execution.tool_name
                        ?.replace("hybrid_processing_", "")
                        .replace("_", " ")}
                    </div>
                    <div className="text-xs lg:text-sm text-gray-500">
                      {new Date(execution.executed_at).toLocaleString()}
                    </div>
                  </div>
                </div>
                <div className="text-xs lg:text-sm text-gray-600">
                  {execution.duration_ms}ms
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default WorkflowManager;
